import React, { useState } from 'react'
import styled from 'styled-components'

import RadioGroup from '../../components/customer-form/radioGroup'
import features from '../../components/features'
import settings from '../../components/settings'

import useTranslation from '../../../utils/translation'
import { isDesigner } from '../../../utils/initValues'
import {
  Heading,
  SubHeading,
  Box,
  Details,
  Link,
  ConfirmContainer,
  RadioGroupContainer,
  BackLink,
  SubStepBackButton,
  StyledCheckBox,
  OptionsContainer,
  StyledTextarea,
  getFirstName,
  useCancelStep2Submit,
  CancelStep2LoadingOverlay,
} from './CancelStep2Shared'
import CancelStep2DesignerDeactivation from './CancelStep2DesignerDeactivation'

const BoxStacked = styled(Box)`
  display: block;
`

const CancelStep2Extra = ({ appointment, cancellationQuestions = [] }) => {
  const t = useTranslation('cancel.step2')
  const store = new URLSearchParams(window.location.search).get('store')
  const isStore = !!store

  const [otherText, setOtherText] = useState('')
  const [selectedQuestionLabel, setSelectedQuestionLabel] = useState('')
  const [selectedQuestion, setSelectedQuestion] = useState(null)
  const [selectedOptions, setSelectedOptions] = useState([])
  const [deactivation, setDeactivation] = useState({
    deactivateCustomer: false,
    deactivationReason: '',
    deactivationTimeframe: '',
  })

  const canDeactivateCustomer =
    features.useDeactivateCustomer &&
    settings.brand === 'magnet' &&
    isDesigner &&
    !appointment?.customerIsInActive

  const showDesignerDeactivation = canDeactivateCustomer && selectedQuestion

  const { runCancel, loading } = useCancelStep2Submit({ appointment, isStore })

  const { deactivateCustomer, deactivationReason, deactivationTimeframe } =
    deactivation

  const deactivationValid =
    !deactivateCustomer || (deactivationReason && deactivationTimeframe)

  const cancel = () => {
    runCancel(
      [selectedQuestion?.label, selectedOptions.join(', '), otherText.trim()]
        .filter(Boolean)
        .join('|'),
      canDeactivateCustomer ? deactivation : undefined
    )
  }

  const onDeactivateCustomerChange = (e) => {
    const { checked } = e.target
    setDeactivation((prev) => ({
      ...prev,
      deactivateCustomer: checked,
      ...(checked ? {} : { deactivationReason: '', deactivationTimeframe: '' }),
    }))
  }

  const onOtherTextChange = (e) => {
    setOtherText(e.target.value)
  }

  const onCancellationQuestionChange = (e) => {
    setSelectedQuestionLabel(e.target.value)
    setSelectedOptions([])
  }

  const goToOptions = () => {
    const question = cancellationQuestions.find(
      (q) => q.label === selectedQuestionLabel
    )
    setSelectedQuestion(question || null)
  }

  const handleOptionChange = (optionText) => {
    setSelectedOptions((prev) =>
      prev.includes(optionText)
        ? prev.filter((o) => o !== optionText)
        : [...prev, optionText]
    )
  }

  const questionRadioOptions = cancellationQuestions.map((q) => ({
    value: q.label,
    label: q.label,
  }))

  if (!cancellationQuestions.length) {
    return null
  }

  return (
    <>
      <Heading>
        {appointment &&
          t('heading', { name: getFirstName(appointment.customerName) })}
      </Heading>
      <BoxStacked>
        <Details cancellationReason="">
          <SubHeading>
            {!selectedQuestion
              ? t('cancellationHeading')
              : selectedQuestion.label}
          </SubHeading>
          <RadioGroupContainer>
            {!selectedQuestion && (
              <RadioGroup
                name="cancellation-main-question"
                value={selectedQuestionLabel}
                options={questionRadioOptions}
                onChange={onCancellationQuestionChange}
              />
            )}
            {selectedQuestion && (
              <>
                {selectedQuestion.options?.length > 0 && (
                  <OptionsContainer
                    style={{
                      marginLeft: 0,
                    }}
                  >
                    {selectedQuestion.options.map((optionText) => (
                      <StyledCheckBox
                        key={optionText}
                        id={`option-${optionText}`}
                        label={optionText}
                        checked={selectedOptions.includes(optionText)}
                        onChange={() => handleOptionChange(optionText)}
                        name={`option-${optionText}`}
                      />
                    ))}
                  </OptionsContainer>
                )}
                <SubHeading style={{ marginTop: '20px', marginBottom: '10px' }}>
                  {t('cancellationReasonHeadingOther')}
                </SubHeading>
                <StyledTextarea
                  isFullWidth
                  value={otherText}
                  onChange={onOtherTextChange}
                />
              </>
            )}
          </RadioGroupContainer>
        </Details>
      </BoxStacked>
      {showDesignerDeactivation && (
        <CancelStep2DesignerDeactivation
          deactivateCustomer={deactivateCustomer}
          onDeactivateCustomerChange={onDeactivateCustomerChange}
          deactivationReason={deactivationReason}
          onDeactivationReasonChange={(value) =>
            setDeactivation((prev) => ({
              ...prev,
              deactivationReason: value,
            }))
          }
          deactivationTimeframe={deactivationTimeframe}
          onDeactivationTimeframeChange={(value) =>
            setDeactivation((prev) => ({
              ...prev,
              deactivationTimeframe: value,
            }))
          }
        />
      )}
      <ConfirmContainer>
        {!selectedQuestion ? (
          <>
            <Link onClick={goToOptions} disabled={!selectedQuestionLabel}>
              {t('next')}
            </Link>
            <BackLink href="/">{t('link')}</BackLink>
          </>
        ) : (
          <>
            <Link onClick={cancel} disabled={!deactivationValid}>
              {t('buttonText')}
            </Link>
            <SubStepBackButton
              type="button"
              onClick={() => setSelectedQuestion(null)}
            >
              {t('back')}
            </SubStepBackButton>
            <BackLink href="/">{t('link')}</BackLink>
          </>
        )}
      </ConfirmContainer>
      <CancelStep2LoadingOverlay loading={loading} />
    </>
  )
}

export default CancelStep2Extra
